import { Link } from "react-router-dom";

type Props = {
  /** "grass" keeps the original dark-green footer, "water" switches to deep ocean blue, "fish" to warm amber brown */
  tone?: "grass" | "water" | "fish";
};

export default function Footer({ tone = "grass" }: Props) {
  const isWater = tone === "water";
  const isFish = tone === "fish";

  const footerSurface = isFish
    ? "bg-[#2a1a0e] border-[#46301a]"
    : isWater
      ? "bg-[#0e2a45] border-[#1d4a6e]"
      : "bg-[#1a2418] border-line";
  const eyebrow = isFish
    ? "text-[#e7c68a]"
    : isWater
      ? "text-[#8fd0ea]"
      : "text-moss";
  const bodyText = isFish
    ? "text-[#dbc6ac]"
    : isWater
      ? "text-[#bcd9ea]"
      : "text-inkDim";
  const iconAccent = isFish
    ? "text-[#e7c68a]"
    : isWater
      ? "text-[#8fd0ea]"
      : "text-moss";
  const linkHover = isFish
    ? "hover:text-[#fdf6ec]"
    : isWater
      ? "hover:text-[#f2f8fc]"
      : "hover:text-ink";
  const socialButton = isFish
    ? "border-[#46301a] text-[#dbc6ac] hover:text-[#fdf6ec] hover:border-[#e7c68a] hover:bg-[#e7c68a]/10"
    : isWater
      ? "border-[#1d4a6e] text-[#bcd9ea] hover:text-[#f2f8fc] hover:border-[#8fd0ea] hover:bg-[#8fd0ea]/10"
      : "border-line text-inkDim hover:text-ink hover:border-moss hover:bg-moss/10";

  return (
    <footer className={`relative border-t ${footerSurface}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 gap-x-8 lg:gap-x-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-6">
              <img
                src="https://storage.helloreaddy.io/project_files/fec1b3e3-c832-4504-ae05-19db151b3fe7/ef790dfe-6718-4f0c-ab04-871fe55886ef_compressed_Logo_Vocatur_GmbH.webp"
                alt="Vocatur GmbH"
                className="h-8 w-auto"
              />
            </div>
            <p className={`text-sm leading-relaxed max-w-sm mb-8 ${bodyText}`}>
              Neue Kunden für Ihren geschäftlichen Erfolg!
            </p>

            <div className={`text-xs uppercase tracking-[0.25em] mb-4 ${eyebrow}`}>
              Folgen Sie uns
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://www.facebook.com/vocatur.de"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Vocatur auf Facebook"
                title="Facebook"
                className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${socialButton}`}
              >
                <i className="fa-brands fa-facebook-f text-base"></i>
              </a>
              <a
                href="https://www.xing.com/pages/vocaturgmbh"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Vocatur auf XING"
                title="XING"
                className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${socialButton}`}
              >
                <i className="fa-brands fa-xing text-base"></i>
              </a>
              <a
                href="https://www.linkedin.com/company/vocatur-gmbh/?lipi=urn%3Ali%3Apage%3Ad_flagship3_search_srp_all%3BY0853meaTE2tfHU7mXx7Ng%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Vocatur auf LinkedIn"
                title="LinkedIn"
                className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${socialButton}`}
              >
                <i className="fa-brands fa-linkedin-in text-base"></i>
              </a>
              <a
                href="https://www.instagram.com/vocatur4ever/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Vocatur auf Instagram"
                title="Instagram"
                className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${socialButton}`}
              >
                <i className="fa-brands fa-instagram text-base"></i>
              </a>
              <a
                href="https://www.handgepackt.de/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="handgepackt eGbR"
                title="handgepackt eGbR"
                className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${socialButton}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="#E11D48">
                  <path d="M12.001 4.52853C14.35 2.42 17.98 2.49 20.2426 4.75736C22.5053 7.02472 22.583 10.637 20.4786 12.993L11.9999 21.485L3.52138 12.993C1.41705 10.637 1.49571 7.01901 3.75736 4.75736C6.02157 2.49315 9.64519 2.41687 12.001 4.52853Z"/>
                </svg>
              </a>
              <a
                href="https://www.com4people.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="com4people GmbH"
                title="com4people GmbH"
                className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${socialButton}`}
              >
                <span className="relative w-5 h-5 flex items-center justify-center shrink-0" aria-hidden="true">
                  <span className="w-4 h-4 rounded-full bg-gradient-to-br from-orange-400 to-orange-600"></span>
                  <i className="ri-leaf-line absolute -top-0.5 right-0 text-[7px] text-green-800 -rotate-12"></i>
                </span>
              </a>
            </div>
          </div>

          {/* Kontakt */}
          <div className="md:col-span-4 md:col-start-5">
            <div className={`text-xs uppercase tracking-[0.25em] mb-5 ${eyebrow}`}>
              Kontaktdaten
            </div>
            <ul className="space-y-3 text-sm">
              <li className={`flex items-start gap-2 ${bodyText}`}>
                <i className={`ri-map-pin-line mt-0.5 ${iconAccent}`}></i>
                <span>
                  Römerstr. 1
                  <br />
                  53424 Remagen
                </span>
              </li>
              <li className={`flex items-center gap-2 ${bodyText}`}>
                <i className={`ri-phone-line ${iconAccent}`}></i>
                +49 2642 948 30 70
              </li>
              <li className={`flex items-center gap-2 ${bodyText}`}>
                <i className={`ri-printer-line ${iconAccent}`}></i>
                +49 2642 948 30 79
              </li>
              <li className={`flex items-center gap-2 ${bodyText}`}>
                <i className={`ri-mail-line ${iconAccent}`}></i>
                <a href="mailto:info@vocatur.de" className={`${linkHover} transition-colors`}>
                  info@vocatur.de
                </a>
              </li>
              <li className={`flex items-center gap-2 ${bodyText}`}>
                <i className={`ri-global-line ${iconAccent}`}></i>
                <a
                  href="https://www.vocatur.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${linkHover} transition-colors`}
                >
                  www.vocatur.de
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-4 md:col-start-9">
            <div className={`text-xs uppercase tracking-[0.25em] mb-5 ${eyebrow}`}>
              Rechtliches
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  to="/impressum"
                  className={`${bodyText} ${linkHover} transition-colors cursor-pointer whitespace-nowrap`}
                >
                  Impressum
                </Link>
              </li>
              <li>
                <Link
                  to="/datenschutz"
                  className={`${bodyText} ${linkHover} transition-colors cursor-pointer whitespace-nowrap`}
                >
                  Datenschutzerklärung
                </Link>
              </li>
              <li>
                <Link
                  to="/haftungsausschluss"
                  className={`${bodyText} ${linkHover} transition-colors cursor-pointer whitespace-nowrap`}
                >
                  Haftungsausschluss
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div
          className={`mt-16 pt-8 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isFish
              ? "border-[#46301a]/70"
              : isWater
                ? "border-[#1d4a6e]/70"
                : "border-line"
          }`}
        >
          <span className={`text-xs ${bodyText}`}>
            © 2026 Vocatur GmbH · Alle Rechte vorbehalten
          </span>
        </div>
      </div>
    </footer>
  );
}